/*
 * Resource PDF upload: the presigned **POST** ticket.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS FILE EXISTS
 * ---------------------------------------------------------------------------
 * `POST /resource/upload` used to answer with `uploadURL`, a presigned S3
 * **PUT**. A presigned PUT signs only bucket, key, method and expiry, so its
 * holder can store any bytes of any type up to S3's 5 TB object limit, and no
 * server-side check can stop it because the bytes never pass through the API.
 * The `application/pdf` content type was advisory only. Every object that lands
 * in the bucket also triggers a Ghostscript page-count Lambda, so the cost of
 * an oversized upload is compute as well as storage.
 *
 * The server replaced it with a presigned **POST** (`upload`), whose signed
 * policy carries a `content-length-range` and a content-type condition that S3
 * enforces *before* it accepts the body. That is the only mechanism S3 offers
 * to bound a browser upload, which is why it needs a client change rather than
 * a quiet server fix.
 *
 * Contract: `docs/contracts/shared/ResourceUploads.md`.
 * Announced in `docs/contracts/inbox/_processed/2026-09-14-resource-upload-presigned-post-and-rate-limit.md`.
 *
 * ---------------------------------------------------------------------------
 * THE THREE RULES THAT ARE EASY TO BREAK
 * ---------------------------------------------------------------------------
 *   1. **`fields` is opaque and ORDERED.** Every entry is covered by the signed
 *      policy. Reordering, renaming, adding or dropping one invalidates the
 *      signature and S3 answers 403, so this module copies the entries out with
 *      `Object.keys` order preserved and never edits them.
 *   2. **The file part goes LAST.** S3 ignores everything after the file part,
 *      so a field appended after it is simply not seen and the policy check
 *      fails on a missing condition.
 *   3. **Do not set `Content-Type` on the request.** The browser has to write
 *      the `multipart/form-data; boundary=...` header itself. Setting it by
 *      hand omits the boundary and the request is unparseable -- this is the
 *      one line that has to be *deleted* when porting the old PUT code, and
 *      the old code set it explicitly.
 *
 * ---------------------------------------------------------------------------
 * NO ANGULAR IMPORTS -- DELIBERATE
 * ---------------------------------------------------------------------------
 * Same reasoning as `http-error.ts`: `@angular/core` is ESM-only from Angular
 * 16 and cannot be `require()`d by ts-node, so anything importing it is
 * unreachable from the layer-0 smoke tests. Everything here is a pure
 * function over plain objects. Covered by `tests/ResourceUpload.smoke.ts`.
 */

/** The presigned POST ticket returned under `upload`. */
export interface ResourceUploadTicket {
  /** Bucket endpoint the multipart form is POSTed to. */
  url: string;
  /** Signed policy fields. Opaque, ordered, never edited. */
  fields: { [name: string]: string };
  /** Hard ceiling S3 will enforce. Read it; never hardcode it. */
  maxBytes: number;
  /** How long the ticket stays valid. */
  expiresInSeconds: number;
}

/** One `fields` entry, flattened so the order is explicit rather than implied. */
export interface UploadField {
  name: string;
  value: string;
}

/**
 * Pull a usable ticket out of the `POST /resource/upload` body.
 *
 * Returns `null` rather than a half-built object when the response cannot be
 * used, so the caller has exactly one thing to test. `uploadURL` is
 * deliberately NOT consulted as a fallback: it is deprecated and the server
 * will delete it, and a silent fallback is how a migration ends up never
 * actually finishing. If `upload` is missing the caller should say so.
 */
export function parseUploadTicket(body: any): ResourceUploadTicket | null {
  if (!body || typeof body !== 'object') { return null; }
  const upload = body.upload;
  if (!upload || typeof upload !== 'object') { return null; }
  if (typeof upload.url !== 'string' || upload.url.length === 0) { return null; }
  if (!upload.fields || typeof upload.fields !== 'object') { return null; }

  const maxBytes = (typeof upload.maxBytes === 'number' && isFinite(upload.maxBytes) && upload.maxBytes > 0)
    ? upload.maxBytes : 0;
  const expiresInSeconds = (typeof upload.expiresInSeconds === 'number' && isFinite(upload.expiresInSeconds))
    ? upload.expiresInSeconds : 0;

  return {
    url: upload.url,
    fields: upload.fields,
    maxBytes: maxBytes,
    expiresInSeconds: expiresInSeconds
  };
}

/**
 * The policy fields, in the order the server sent them.
 *
 * `Object.keys` preserves insertion order for string keys, which is the order
 * they were parsed out of the JSON, which is the order the server wrote them.
 * Values are stringified because S3 compares them as text and a numeric
 * `content-length-range` bound arriving as a number would be sent as `[object
 * Object]`-adjacent nonsense by some form implementations.
 */
export function uploadFieldEntries(ticket: ResourceUploadTicket): UploadField[] {
  if (!ticket || !ticket.fields) { return []; }
  return Object.keys(ticket.fields).map(name => ({ name: name, value: String(ticket.fields[name]) }));
}

/**
 * True when the file cannot possibly be accepted.
 *
 * Checked client-side so the user gets a sentence instead of S3's
 * `EntityTooLarge` XML document, which the browser renders as a blank failure.
 * `maxBytes === 0` means the server did not state a limit; in that case we do
 * not invent one -- let S3 decide rather than refuse a file it would accept.
 */
export function exceedsMaxBytes(sizeBytes: number, ticket: ResourceUploadTicket): boolean {
  if (!ticket || !ticket.maxBytes || ticket.maxBytes <= 0) { return false; }
  if (typeof sizeBytes !== 'number' || !isFinite(sizeBytes)) { return false; }
  return sizeBytes > ticket.maxBytes;
}

/**
 * Binary-prefixed size for display: `1073741824` -> `1 GB`.
 *
 * Deliberately plain ASCII digits -- the caller runs the result through
 * `LanguagePipe.localizeNumber` when it builds the sentence, because five
 * locales (`ar`, `am`, `bn`, `hi`, `ta`) declare a non-Western digit table and
 * a raw numeral renders in the wrong script there.
 */
export function formatBytes(bytes: number): string {
  if (typeof bytes !== 'number' || !isFinite(bytes) || bytes <= 0) { return ''; }
  const units = ['bytes', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value = value / 1024;
    unit++;
  }
  const rounded = unit === 0 ? value : Math.round(value * 10) / 10;
  return `${rounded} ${units[unit]}`;
}

/** Build the multipart body. Fields first, unchanged and in order; file last. */
export function buildUploadForm(ticket: ResourceUploadTicket, file: File, fieldName: string = 'file'): FormData {
  const form = new FormData();
  for (const field of uploadFieldEntries(ticket)) {
    form.append(field.name, field.value);
  }
  // MUST be last: S3 ignores every part after the file.
  form.append(fieldName, file);
  return form;
}

/**
 * Why S3 refused, as a stable code for branching.
 *
 * S3 answers a rejected POST with an XML document, not JSON, and the HTTP
 * status alone does not distinguish "too big" (recoverable by the user, pick a
 * smaller file) from "the policy did not match" (a bug on our side or an
 * expired ticket, and re-trying is pointless). The discriminator is the
 * `<Code>` element.
 *
 *   TOO_LARGE  -- over `maxBytes`. Tell the user the limit.
 *   EXPIRED    -- the ticket aged out. A fresh ticket will work.
 *   REJECTED   -- policy condition failed / malformed form. Our bug; report it.
 *   FAILED     -- anything else, including a network drop (status 0).
 */
export type UploadFailureKind = 'TOO_LARGE' | 'EXPIRED' | 'REJECTED' | 'FAILED';

export function classifyUploadFailure(status: number, responseText: string): UploadFailureKind {
  const text = typeof responseText === 'string' ? responseText : '';
  if (text.indexOf('EntityTooLarge') >= 0) { return 'TOO_LARGE'; }
  if (text.indexOf('ExpiredToken') >= 0 || text.indexOf('RequestTimeTooSkewed') >= 0
      || text.indexOf('Policy expired') >= 0) { return 'EXPIRED'; }
  if (status === 400 || status === 403) { return 'REJECTED'; }
  return 'FAILED';
}

/** A POST to S3 succeeded only on 204; `2xx` generally is accepted defensively. */
export function isUploadSuccess(status: number): boolean {
  return status >= 200 && status < 300;
}

